import type {
  TaskExecutionStatus,
  TaskExecutionSummary,
  UserSummary,
} from '@/lib/api/generated/model';
import { costOfRuns, type TaskCost } from './executionHistory';
import { isTerminalStatus } from './executionStatus';

/**
 * A task's run history as the person thinks of it: attempts, each a trip
 * through the workflow, with the bookkeeping folded away (spec 127 US1).
 *
 * Nothing here is stored by the service. It is read off what it already
 * writes: every time a task comes back to Backlog a `skipped` execution of
 * kind `backlog` is recorded, and that marker closes an attempt (research R1,
 * checked against a 62-execution history). Human gates and Done are written
 * as `skipped` too when a task passes them; consecutive skipped executions are
 * one "passed through" line rather than a row each.
 */
export type HistoryItem =
  | { kind: 'run'; execution: TaskExecutionSummary }
  | {
      kind: 'pass-through';
      executions: TaskExecutionSummary[];
      /** Step ids passed, oldest first, without repeats. */
      steps: string[];
    };

export type AttemptOutcome =
  'completed' | 'failed' | 'stopped' | 'blocked' | 'queued' | 'live' | 'passed';

export interface Attempt {
  /** 1 is the oldest. */
  ordinal: number;
  /** Newest first, as the tab lists runs. */
  items: HistoryItem[];
  startedAt: string | null;
  /**
   * Whom the attempt's first run is attributed to (spec 158): the person who
   * set this trip through the workflow going. The run `startedAt` reads.
   */
  startedBy: UserSummary | null;
  outcome: AttemptOutcome;
  /** Time spent running, summed over the attempt's runs; waits are not work. */
  workMs: number;
  cost: TaskCost;
  hasFailure: boolean;
  isLive: boolean;
}

const OUTCOME_OF: Partial<Record<TaskExecutionStatus, AttemptOutcome>> = {
  completed: 'completed',
  failed: 'failed',
  stopped: 'stopped',
  blocked: 'blocked',
  queued: 'queued',
};

function oldestFirst(
  executions: readonly TaskExecutionSummary[],
): TaskExecutionSummary[] {
  return [...executions].sort((a, b) =>
    a.created_at.localeCompare(b.created_at),
  );
}

/** Newest attempt first. */
export function groupExecutions(
  executions: readonly TaskExecutionSummary[],
): Attempt[] {
  const groups: TaskExecutionSummary[][] = [];
  let current: TaskExecutionSummary[] = [];
  for (const execution of oldestFirst(executions)) {
    current.push(execution);
    // Coming back to Backlog ends the trip.
    if (execution.step_kind_snapshot === 'backlog') {
      groups.push(current);
      current = [];
    }
  }
  if (current.length > 0) groups.push(current);

  return groups.map((runs, index) => summarise(runs, index + 1)).reverse();
}

function summarise(
  executionsOldestFirst: TaskExecutionSummary[],
  ordinal: number,
): Attempt {
  const itemsOldestFirst: HistoryItem[] = [];
  for (const execution of executionsOldestFirst) {
    const last = itemsOldestFirst.at(-1);
    if (execution.status === 'skipped') {
      if (last?.kind === 'pass-through') {
        last.executions.push(execution);
        if (last.steps.at(-1) !== execution.step_id_snapshot) {
          last.steps.push(execution.step_id_snapshot);
        }
      } else {
        itemsOldestFirst.push({
          kind: 'pass-through',
          executions: [execution],
          steps: [execution.step_id_snapshot],
        });
      }
    } else {
      itemsOldestFirst.push({ kind: 'run', execution });
    }
  }

  const runs = executionsOldestFirst.filter((e) => e.status !== 'skipped');
  const lastRun = runs.at(-1);
  const isLive = runs.some((e) => !isTerminalStatus(e.status));
  const outcome: AttemptOutcome = isLive
    ? lastRun?.status === 'queued'
      ? 'queued'
      : 'live'
    : lastRun
      ? (OUTCOME_OF[lastRun.status] ?? 'completed')
      : 'passed';

  let workMs = 0;
  for (const run of runs) {
    if (!run.started_at || !run.ended_at) continue;
    const span = Date.parse(run.ended_at) - Date.parse(run.started_at);
    if (Number.isFinite(span) && span > 0) workMs += span;
  }

  return {
    ordinal,
    items: itemsOldestFirst.reverse(),
    startedAt:
      executionsOldestFirst[0]?.started_at ??
      executionsOldestFirst[0]?.created_at ??
      null,
    startedBy: executionsOldestFirst[0]?.started_by ?? null,
    outcome,
    workMs,
    cost: costOfRuns(runs),
    hasFailure: runs.some((e) => e.status === 'failed'),
    isLive,
  };
}
